(() => {
  const prefersReduced =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) return;

  let cleanup = null;

  function init() {
    if (cleanup) cleanup();

    const hero = document.getElementById("paradise-hero");
    const heroWordmark = document.getElementById("hero-wordmark");
    const headerLogoLink = document.querySelector("a.header-logo");
    const headerComponent = document.getElementById("header-component");

    if (!hero || !heroWordmark || !headerLogoLink) return false;

    document.documentElement.classList.add("wordmark-scrub-enabled");

    // Trigger points
    const DOWN_TRIGGER = 40;
    const UP_TRIGGER = 10;

    // Animation
    const DURATION = 650;
    const EASE = "cubic-bezier(.2,.9,.15,1)";

    const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
    const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    function getVisibleHeaderLogoContainer() {
      const original = headerLogoLink.querySelector('[data-logo-target="original"]');
      const inverse = headerLogoLink.querySelector('[data-logo-target="inverse"]');

      if (inverse) {
        const s = getComputedStyle(inverse);
        if (s.display !== "none" && s.visibility !== "hidden") return inverse;
      }
      return original || null;
    }

    // state
    let mode = "hero"; // "hero" | "header"
    let lastY = window.scrollY || 0;
    let isAnimating = false;
    let raf = null;

    function setTransitions(on) {
      const headerLogo = getVisibleHeaderLogoContainer();
      if (!headerLogo) return;

      const t = on ? `transform ${DURATION}ms ${EASE}, opacity ${DURATION}ms ease` : "";

      heroWordmark.style.transition = t;
      headerLogo.style.transition = t;

      const other = headerLogoLink.querySelector(
        headerLogo.matches('[data-logo-target="inverse"]')
          ? '[data-logo-target="original"]'
          : '[data-logo-target="inverse"]'
      );
      if (other) other.style.transition = t;
    }

    function syncBothLogoVariants(transform, opacity) {
      const headerLogo = getVisibleHeaderLogoContainer();
      if (!headerLogo) return;

      const other = headerLogoLink.querySelector(
        headerLogo.matches('[data-logo-target="inverse"]')
          ? '[data-logo-target="original"]'
          : '[data-logo-target="inverse"]'
      );

      headerLogo.style.transform = transform;
      headerLogo.style.opacity = String(opacity);
      if (other) {
        other.style.transform = transform;
        other.style.opacity = String(opacity);
      }
    }

    function baseStateHero() {
      // hero visible, header hidden
      setTransitions(false);
      heroWordmark.style.transform = "translate(0px, 0px) scale(1)";
      heroWordmark.style.opacity = "1";
      syncBothLogoVariants("translate(0px, 0px) scale(0.85)", 0);
    }

    function baseStateHeader() {
      // header visible, hero hidden
      setTransitions(false);
      heroWordmark.style.transform = "translate(0px, 0px) scale(0.85)";
      heroWordmark.style.opacity = "0";
      syncBothLogoVariants("translate(0px, 0px) scale(1)", 1);
    }

    function computeDeltas() {
      const headerLogo = getVisibleHeaderLogoContainer();
      if (!headerLogo) return null;

      // IMPORTANT: measure with current transforms cleared (we manage them)
      const heroRect = heroWordmark.getBoundingClientRect();
      const headRect = headerLogo.getBoundingClientRect();

      const heroCx = heroRect.left + heroRect.width / 2;
      const heroCy = heroRect.top + heroRect.height / 2;

      const headCx = headRect.left + headRect.width / 2;
      const headCy = headRect.top + headRect.height / 2;

      const dx = headCx - heroCx;
      const dy = headCy - heroCy;

      // scale ratios (keep within sane limits so it doesn’t explode on mobile)
      const heroToHeadScale = clamp(headRect.width / heroRect.width, 0.05, 2);
      const headToHeroScale = clamp(heroRect.width / headRect.width, 0.05, 2);

      return { dx, dy, heroToHeadScale, headToHeroScale };
    }

    async function animateToHeader() {
      if (isAnimating) return;
      const headerLogo = getVisibleHeaderLogoContainer();
      if (!headerLogo) return;

      const d = computeDeltas();
      if (!d) return;

      isAnimating = true;

      // Start: hero normal, header logo sitting ON TOP of hero (big), but invisible
      setTransitions(false);

      heroWordmark.style.transformOrigin = "center center";
      headerLogo.style.transformOrigin = "center center";
      heroWordmark.style.willChange = "transform, opacity";
      headerLogo.style.willChange = "transform, opacity";

      heroWordmark.style.transform = "translate(0px, 0px) scale(1)";
      heroWordmark.style.opacity = "1";

      // Put header logo over hero: move opposite of dx/dy and scale to match hero size
      syncBothLogoVariants(`translate(${-d.dx}px, ${-d.dy}px) scale(${d.headToHeroScale})`, 0);

      await nextFrame();
      await nextFrame(); // ensures browser commits start state

      // Animate both
      setTransitions(true);

      // hero flies to header and shrinks to header size
      heroWordmark.style.transform = `translate(${d.dx}px, ${d.dy}px) scale(${d.heroToHeadScale})`;
      heroWordmark.style.opacity = "0";

      // header logo flies to its place and becomes normal size
      syncBothLogoVariants("translate(0px, 0px) scale(1)", 1);

      await wait(DURATION);

      mode = "header";
      // Snap to clean base (prevents drift if user keeps scrolling)
      baseStateHeader();

      isAnimating = false;
    }

    async function animateToHero() {
      if (isAnimating) return;
      const headerLogo = getVisibleHeaderLogoContainer();
      if (!headerLogo) return;

      const d = computeDeltas();
      if (!d) return;

      isAnimating = true;

      // Start: header normal, hero sitting ON TOP of header (small), invisible
      setTransitions(false);

      // Header is visible at its place
      syncBothLogoVariants("translate(0px, 0px) scale(1)", 1);

      // Put hero wordmark over header (move to header position and scale to header size)
      heroWordmark.style.transformOrigin = "center center";
      heroWordmark.style.willChange = "transform, opacity";
      heroWordmark.style.transform = `translate(${d.dx}px, ${d.dy}px) scale(${d.heroToHeadScale})`;
      heroWordmark.style.opacity = "0";

      await nextFrame();
      await nextFrame();

      // Animate both
      setTransitions(true);

      // Header flies down to hero position and grows to match hero size, then fades out
      syncBothLogoVariants(`translate(${-d.dx}px, ${-d.dy}px) scale(${d.headToHeroScale})`, 0);

      // Hero flies back to its normal spot and becomes visible
      heroWordmark.style.transform = "translate(0px, 0px) scale(1)";
      heroWordmark.style.opacity = "1";

      await wait(DURATION);

      mode = "hero";
      baseStateHero();

      isAnimating = false;
    }

    function tick() {
      raf = null;
      const y = window.scrollY || 0;
      const goingDown = y > lastY;
      const goingUp = y < lastY;

      if (!isAnimating && goingDown && y > DOWN_TRIGGER && mode !== "header") {
        animateToHeader();
      }

      if (!isAnimating && goingUp && y < UP_TRIGGER && mode !== "hero") {
        animateToHero();
      }

      lastY = y;
    }

    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(tick);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // When transparent header flips original/inverse logo, reset cleanly
    let mo = null;
    if (headerComponent) {
      mo = new MutationObserver(() => {
        if (isAnimating) return;
        if (mode === "header") baseStateHeader();
        else baseStateHero();
      });
      mo.observe(headerComponent, { attributes: true, subtree: true });
    }

    // Initial state
    if ((window.scrollY || 0) > DOWN_TRIGGER) {
      mode = "header";
      baseStateHeader();
    } else {
      mode = "hero";
      baseStateHero();
    }

    cleanup = () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (mo) mo.disconnect();
      raf = null;
      document.documentElement.classList.remove("wordmark-scrub-enabled");
    };

    return true;
  }

  function boot() {
    let tries = 0;
    const timer = setInterval(() => {
      tries++;
      const ok = init();
      if (ok || tries > 200) clearInterval(timer);
    }, 50);
  }

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", boot);
  boot();
})();
